import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ListDTO } from '@translate-ai/shared';
import {
  LIST_DESCRIPTION_MAX_LENGTH,
  LIST_NAME_MAX_LENGTH,
} from '@translate-ai/shared';
import {
  useCreateList,
  useItemLists,
  useLists,
  useSetItemLists,
} from '@/api/hooks';
import { BackButton } from '@/components/BackButton';
import { ErrorMessage } from '@/components/ErrorMessage';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';
import { ApiError } from '@/api/client';

function localizeListName(list: ListDTO, t: (key: string) => string): string {
  if (list.isSystem && list.systemKey === 'favorites') return t('lists.favorites');
  return list.name;
}

export function ItemListsPicker() {
  const { translationId } = useParams<{ translationId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lists = useLists();
  const itemLists = useItemLists(translationId);
  const setLists = useSetItemLists();
  const create = useCreateList();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; description: string } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!seeded && itemLists.data) {
      setSelected(new Set(itemLists.data.listIds));
      setSeeded(true);
    }
  }, [itemLists.data, seeded]);

  useEffect(() => {
    if (draft) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [draft]);

  const orderedLists = useMemo(() => lists.data?.items ?? [], [lists.data]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startNew = () => {
    setCreateError(null);
    setDraft({ name: '', description: '' });
  };

  const submitCreate = async () => {
    if (!draft) return;
    setCreateError(null);
    try {
      const created = await create.mutateAsync({
        name: draft.name.trim(),
        description: draft.description.trim() || null,
      });
      setSelected((prev) => {
        const next = new Set(prev);
        next.add(created.id);
        return next;
      });
      setDraft(null);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  const handleSave = async () => {
    if (!translationId) return;
    setError(null);
    try {
      await setLists.mutateAsync({
        translationId,
        listIds: [...selected],
      });
      navigate(-1);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TRANSLATION_NOT_FOUND') {
        setError(t('lists.errors.translationGone'));
        setTimeout(() => navigate(-1), 1500);
        return;
      }
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  const isLoading = lists.isLoading || itemLists.isLoading || !seeded;

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center justify-between">
        <BackButton onClick={() => navigate(-1)} />
      </div>

      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">{t('lists.addToListsTitle')}</h1>
          <p className="text-sm text-slate-500">{t('lists.addToListsHint')}</p>
        </div>
        {!draft && (
          <button className="btn-primary h-8 shrink-0 px-3 text-sm" onClick={startNew}>
            {t('lists.newList')}
          </button>
        )}
      </header>

      {isLoading ? (
        <LoadingPlaceholder />
      ) : (
        <ul className="space-y-2">
          {orderedLists.map((list) => {
            const checked = selected.has(list.id);
            return (
              <li key={list.id}>
                <label className="card flex items-center gap-3 p-3 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={checked}
                    onChange={() => toggle(list.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-900">
                      {localizeListName(list, t)}
                    </p>
                    {list.description && (
                      <p className="line-clamp-1 text-xs text-slate-500">
                        {list.description}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    {list.itemCount}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {draft && (
        <div ref={formRef} className="card space-y-2 p-3">
          <input
            className="input"
            placeholder={t('lists.namePlaceholder')}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            maxLength={LIST_NAME_MAX_LENGTH}
            autoFocus
          />
          <textarea
            className="input min-h-[80px]"
            placeholder={t('lists.descriptionPlaceholder')}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            maxLength={LIST_DESCRIPTION_MAX_LENGTH}
          />
          <ErrorMessage message={createError} />
          <div className="flex justify-end gap-2">
            <button
              className="btn-ghost h-9 px-3 text-sm"
              onClick={() => {
                setDraft(null);
                setCreateError(null);
              }}
            >
              {t('lists.cancel')}
            </button>
            <button
              className="btn-primary h-9 px-3 text-sm"
              onClick={submitCreate}
              disabled={create.isPending || !draft.name.trim()}
            >
              {t('lists.create')}
            </button>
          </div>
        </div>
      )}

      <ErrorMessage message={error} />

      <button
        className="btn-primary w-full"
        onClick={handleSave}
        disabled={setLists.isPending || isLoading}
      >
        {t('lists.save')}
      </button>
    </div>
  );
}
